import type { ReactNode } from 'react';

/**
 * Hides UI the user cannot act on.
 *
 * This is a USABILITY affordance and nothing more. The identical permission is
 * enforced server-side on the endpoint, and removing this component would
 * change what a user sees, never what they can do. Treating it as a security
 * control is the mistake RULE 1 exists to prevent — "the frontend is not the
 * security layer".
 *
 * The reason it still earns its place: showing a button that always returns
 * 403 teaches people to ignore errors.
 */
export function PermissionGate({ has, need, children, fallback = null }: {
  /** The permissions the current principal holds. */
  has: ReadonlySet<string> | string[];
  /** Any one of these is enough. */
  need: string | string[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const held = has instanceof Set ? has : new Set(has);
  const required = Array.isArray(need) ? need : [need];

  const allowed = required.some((p) => {
    if (held.has(p)) return true;
    // Domain wildcard, matching the server guard: plant.* covers plant.create.
    const domain = p.split('.')[0];
    return held.has(`${domain}.*`) || held.has('*');
  });

  return <>{allowed ? children : fallback}</>;
}
